import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { createBatchOverride, deleteBatchOverride } from "../../../../../services/BatchOverrideService.js";
import { getBatches } from "../../../../../services/BatchService.js";
import { getClassrooms } from "../../../../../services/ClassroomService.js";
import { getOverridesList } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import Modal from "../../components/Modal.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import Pager from "../../components/Pager.jsx";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import { confirm as confirmDialog } from "../../../../components/common/Confirm/confirm";
import OverrideRecordModal from "./OverrideRecordModal.jsx";

const PAGE_SIZE = 20;
const DASH = "—";

const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString() : DASH);

const RoomOverridesSection = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("list-classroom", ["update"], "lms");

  const [overrides, setOverrides] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ batchId: "", date: "", action: "moved", newClassroom: "", reason: "" });
  // The override being read, or null. The row carries the whole
  // record -- there is no detail endpoint behind an override -- so the
  // popup opens with it rather than fetching it again.
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getOverridesList(page, PAGE_SIZE, search);
      setOverrides(result.data || []);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading shift overrides:", error);
      toast.error("Failed to load shift overrides");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = useDebouncedCallback((value) => {
    setSearch(value.trim());
    setPage(1);
  });

  /* The record popup stays up under the confirm (which sits above it) and
     closes only after the change: on success it shows the done panel and
     closes itself; on cancel or error it is still there. */
  const handleDelete = async (id) => {
    const confirm = await confirmDialog({
      title: "Revert this override?",
      text: "The batch will go back to its original room and schedule for this date.",
      confirmButtonText: "Yes, revert it",
      tone: "danger",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteBatchOverride(id);
      toast.success("Override reverted");
      load();
    } catch (error) {
      toast.error("Failed to revert override");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.batchId || !form.date || !form.reason || (form.action === "moved" && !form.newClassroom)) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, date: new Date(form.date).toISOString() };
      if (form.action === "cancelled") delete payload.newClassroom;
      await createBatchOverride(payload);
      toast.success("Override created");
      setForm({ batchId: "", date: "", action: "moved", newClassroom: "", reason: "" });
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message?.message || "Failed to create override");
    } finally {
      setSaving(false);
    }
  };

  /* No actions column: the row opens the record and Revert lives in
     that popup's footer, which is how every other list here works
     (view_popup_rules). Revert used to sit on the row -- one click
     away from undoing a change, with the reason for it clipped to
     whatever the column had left. */
  const headers = useMemo(
    () => [
      {
        title: "Date",
        prop: "date",
        isSortable: true,
        cell: (row) => <span className="pg-date">{dateLabel(row.date)}</span>,
      },
      {
        title: "Batch",
        prop: "batchName",
        isSortable: true,
        cell: (row) => <span className="pg-strong">{row.batchName || DASH}</span>,
      },
      {
        title: "Action",
        prop: "action",
        isSortable: true,
        cell: (row) =>
          row.action === "moved" ? (
            <StatusChip status="shifted">Shifted to {row.newClassroomNo}</StatusChip>
          ) : (
            <StatusChip status="maintenance">Cancelled for the day</StatusChip>
          ),
      },
      {
        title: "Reason",
        prop: "reason",
        cell: (row) =>
          row.reason ? (
            <span className="lp-plain">{row.reason}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
    ],
    [],
  );

  return (
    <section className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h2 className="pg-title">Room shifts &amp; one-day cancellations</h2>
          <p className="pg-sub">
            A batch moved to another room, or called off, for a single date.
          </p>
        </div>

        {canEdit && (
          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setShowModal(true)}
            >
              New override
            </button>
          </div>
        )}
      </header>

      <section className="pg-filters" aria-label="Filter overrides">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="ovr-search">
              Search
            </label>
            <input
              id="ovr-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by reason..."
              value={uiSearchTerm}
              onChange={(e) => {
                setUiSearchTerm(e.target.value);
                runSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </section>

      {!loading && overrides.length === 0 ? (
        <div className="pg-state">
          <h3 className="pg-state-title">
            {search ? "Nothing matched" : "No overrides recorded"}
          </h3>
          <p className="pg-state-text">
            {search
              ? `No override matches “${search}”.`
              : "Nothing has been moved or cancelled; every batch is running to its timetable."}
          </p>
        </div>
      ) : (
        <>
          <div className="td-list td-list--overrides">
            <NewCustomDataTable
              data={overrides}
              headers={headers}
              totalRecords={pagination?.total ?? overrides.length}
              currentPage={page}
              recordsPerPage={PAGE_SIZE}
              serverSide
              loading={loading}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setDetail(row)}
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: "button",
                "aria-label": `Open the override on ${row.batchName || "this batch"}`,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetail(row);
                  }
                },
              })}
            />
          </div>

          <Pager pagination={pagination} onPageChange={setPage} />
        </>
      )}

      {detail && (
        <OverrideRecordModal
          record={detail}
          canEdit={canEdit}
          onClose={() => setDetail(null)}
          onRevert={(row) => handleDelete(row.id)}
        />
      )}

      {showModal && (
        <Modal
          eyebrow="Override"
          title="New shift or cancellation"
          sub="Which batch, which date, and what happens to it."
          onClose={() => setShowModal(false)}
          size="form"
          labelledBy="cls-override-title"
          footer={
            <>
              <div className="pgm-actions">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
              <button
                type="submit"
                form="cls-override-form"
                className="pg-btn pg-btn--main"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save override"}
              </button>
            </>
          }
        >
          <form id="cls-override-form" className="att-form" noValidate onSubmit={submit}>
            <div className="mcq-options">
              <div className="pg-field">
                <label className="pg-label" htmlFor="ovr-batch">
                  Batch
                </label>
                <div id="ovr-batch">
                  <AsyncPagedSelect
                    value={form.batchId}
                    onChange={(id) => setForm((f) => ({ ...f, batchId: id }))}
                    fetchPage={(p, q) => getBatches(20, p, q)}
                    toOption={(b) => ({ value: b.id, label: b.name })}
                    placeholder="Select batch"
                  />
                </div>
              </div>

              <div className="pg-field">
                <label className="pg-label" htmlFor="ovr-date">
                  Date
                </label>
                <input
                  id="ovr-date"
                  type="date"
                  className="pg-input"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                />
              </div>

              <div className="pg-field">
                <label className="pg-label" htmlFor="ovr-action">
                  Action
                </label>
                <select
                  id="ovr-action"
                  className="pg-select"
                  value={form.action}
                  onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
                >
                  <option value="moved">Move to another classroom</option>
                  <option value="cancelled">Cancel for this date</option>
                </select>
              </div>

              {form.action === "moved" && (
                <div className="pg-field">
                  <label className="pg-label" htmlFor="ovr-room">
                    New classroom
                  </label>
                  <div id="ovr-room">
                    <AsyncPagedSelect
                      value={form.newClassroom}
                      onChange={(id) => setForm((f) => ({ ...f, newClassroom: id }))}
                      fetchPage={(p, q) => getClassrooms(20, p, q)}
                      toOption={(c) => ({ value: c.id, label: c.classroomNo })}
                      placeholder="Select classroom"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="ovr-reason">
                Reason
              </label>
              <textarea
                id="ovr-reason"
                className="pg-input mcq-textarea"
                rows={2}
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Projector failure in Lab 12"
              />
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
};

export default RoomOverridesSection;
